import { selectFocused, useWindowStore } from '@/wm/store';

/**
 * The browser's Back button, handed to Internet Explorer.
 *
 * There is a browser inside this page, and the person using it navigated it.
 * When that window is on top and it has somewhere to go back to, Back means
 * *its* Back — which covers the toolbar button, `Alt+Left`, the fourth button
 * on a mouse and a two-finger swipe, because on the web those are all one
 * gesture by the time the page hears about them: a `popstate`.
 *
 * This is deliberately narrower than turning Back into "close the top window",
 * which `useDeepLinkSync` declines to do for good reason. Nothing else on this
 * desktop claims the key. Back with anything else focused, or with Internet
 * Explorer sitting on the first page it opened, leaves the site — as it should.
 *
 * ── How it works ────────────────────────────────────────────────────────────
 * A page can only respond to Back if there is an entry for the press to
 * consume, so while the rule above holds, one spare entry is kept pushed on the
 * stack. Pressing Back consumes it, `popstate` fires, Internet Explorer goes
 * back a page, and a fresh spare is pushed to replace it. The address bar never
 * moves, because the spare carries the same URL as the entry beneath it.
 *
 * When the rule stops holding — the window loses focus, or runs out of history
 * — the spare is taken away again, which is what lets the *next* Back leave the
 * site instead of being silently swallowed.
 *
 * ── What this cannot do ─────────────────────────────────────────────────────
 * Forward. Pushing the replacement spare truncates the forward stack, so after
 * a Back there is never a forward entry for the browser's Forward button to
 * visit, and it stays dead. Internet Explorer's own Forward button is
 * unaffected and is the one that works.
 *
 * A click inside a framed site is also beyond this: a cross-origin frame's
 * navigations join the same session history, so the first Back may well go back
 * inside the frame rather than reaching this code. That is the browser's own
 * behaviour and arguably the right one.
 */

/** What a window has to provide to be handed the Back button. */
export interface BackHandler {
  canGoBack(): boolean;
  goBack(): void;
}

/** Marks the spare entry, so a reload can tell it is standing on one. */
const SENTINEL = 'teonio:back';

const handlers = new Map<string, BackHandler>();

/** True while a spare entry is on the stack. */
let armed = false;
/** True while `disarm()` is waiting for the `popstate` it caused itself. */
let disarming = false;
let installed = false;

function here(): string {
  return window.location.pathname + window.location.search;
}

/** The handler belonging to the focused window, if it has one. */
function activeHandler(): BackHandler | undefined {
  const focused = selectFocused(useWindowStore.getState());
  return focused ? handlers.get(focused.id) : undefined;
}

function wantsBack(): boolean {
  return activeHandler()?.canGoBack() === true;
}

function arm(): void {
  if (armed) return;
  armed = true;
  try {
    // Same URL as the entry beneath it: the spare is a place to stand, not a
    // place to be. `pushState` also truncates the forward stack, which is what
    // clears away a spare left behind by an earlier `disarm`.
    window.history.pushState({ teonio: SENTINEL }, '', here());
  } catch {
    // Rate-limited, or a sandboxed context with no history access. Back simply
    // goes on meaning what it meant before.
    armed = false;
  }
}

function disarm(): void {
  if (!armed) return;
  armed = false;
  disarming = true;
  try {
    // Consumes our own spare. The entry underneath carries the same URL, so
    // nothing on screen moves.
    window.history.back();
  } catch {
    disarming = false;
  }
}

/** Bring the stack into line with whatever is focused now. */
function sync(): void {
  if (wantsBack()) arm();
  else disarm();
}

/**
 * Offer the Back button to a window, or take the offer away.
 *
 * Called again whenever the answer to `canGoBack` changes, which is what keeps
 * the spare entry in step with the history it stands for: re-registering
 * re-runs `sync`, so a window that has just run out of pages gives the button
 * back to the browser.
 */
export function registerBackHandler(windowId: string, handler: BackHandler | null): void {
  if (handler) handlers.set(windowId, handler);
  else handlers.delete(windowId);
  if (installed) sync();
}

function onPopState(): void {
  // The `popstate` from our own `disarm`. Nothing to do; the spare is gone,
  // which is exactly what was wanted.
  if (disarming) {
    disarming = false;
    return;
  }

  // Something consumed the spare, which is the only entry a Back press can
  // reach while this is armed.
  armed = false;

  const handler = activeHandler();
  if (!handler?.canGoBack()) return;

  handler.goBack();
  // Straight back on, without waiting to see whether there is any history
  // left: a gap here is a Back press that would fall through and leave the
  // site. The window re-registers a moment later once it has re-rendered, and
  // `sync` takes the spare away then if it turns out to be the last page.
  arm();
}

/**
 * Wire the Back button up. Returns the teardown, in the shape every other
 * listener installer in the shell uses.
 */
export function installBackButton(): () => void {
  if (typeof window === 'undefined') return () => {};

  // A reload lands back on the spare entry if there was one, and the flag it
  // is marked with is the only way to know that. Without this the entry would
  // be orphaned, and one Back press would reload the desktop rather than
  // leaving it.
  armed = (window.history.state as { teonio?: string } | null)?.teonio === SENTINEL;
  disarming = false;
  installed = true;

  window.addEventListener('popstate', onPopState);
  const unsubscribe = useWindowStore.subscribe(sync);
  sync();

  return () => {
    installed = false;
    // Unhooked *before* the spare is taken away, so the `popstate` that comes
    // back a task later belongs to nobody. Calling `disarm()` here instead
    // would leave `disarming` set with no listener to clear it, and the next
    // install would swallow the first real Back press it saw.
    window.removeEventListener('popstate', onPopState);
    unsubscribe();
    handlers.clear();

    const hadSpare = armed;
    armed = false;
    disarming = false;
    if (!hadSpare) return;
    try {
      window.history.back();
    } catch {
      // Nothing to undo, which is the state we were heading for anyway.
    }
  };
}
